import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { SKIP_ERROR_TRACKING, SKIP_ERROR_TRACKING_STATUSES } from '@lite-workspace/shared/services/analytics';
import { EXPECTED_FORBIDDEN } from '../../../../core/http-context.token';
import type { ImportOverrides, ImportReport, ImportSet, ProfileId, TableTargetField } from '@lite-workspace/import-pipeline-core';

/**
 * Зеркало ImportRunStatusRestApi гейтвея: EXTRACTING — сервер ещё распаковывает пакет и предпросмотра
 * пока нет; PARTIAL — apply прошёл, часть файлов ждёт распознавания.
 */
export type ImportRunStatus = 'EXTRACTING' | 'PREVIEW' | 'APPLYING' | 'APPLIED' | 'PARTIAL' | 'FAILED';

/** Зеркало ImportRunFailureReasonRestApi: почему прогон FAILED — мастер показывает честный текст, а не гадает. */
export type ImportRunFailureReason =
  | 'entry_limit'
  | 'total_size_limit'
  | 'entry_too_large'
  | 'extract_timeout'
  | 'stale'
  | 'internal'
  | 'manifest_missing';

export interface ImportPreviewInput {
  fileIds?: string[];
  uploadBatchId?: string;
  /** Архив импорта: id уже загруженного zip-объекта; разбор идёт на сервере. */
  archiveFileId?: string;
  /** Профиль `table`: текст таблицы. Байты декодирует клиент — только у него они есть. */
  content?: string;
}

export interface ImportPreviewRequest {
  profileId?: ProfileId;
  input?: ImportPreviewInput;
  /** Ручной маппинг колонок: заголовок → цель. Действует внутри разбора, поэтому едет не в overrides. */
  mapping?: Record<string, TableTargetField>;
}

export interface ImportPreviewResult {
  runId: string;
  status: ImportRunStatus;
  set: ImportSet;
}

export interface ImportRun {
  id: string;
  projectId: string;
  profileId: string;
  sourceKey: string;
  status: ImportRunStatus;
  failureReason?: ImportRunFailureReason;
  uploadBatchId?: string;
  overrides?: ImportOverrides;
  set?: ImportSet;
  report?: ImportReport;
  createdAt?: string;
  updatedAt?: string;
}

interface PersonImportConfig {
  readonly enabled?: boolean;
}

/** Ответ досчёта вместе с часами СЕРВЕРА: `updatedAt` прогона снят с них же, и сравнивать его с
 *  часами браузера нельзя — ушедшая вперёд вкладка выдала бы только что дописанный прогон за вчерашний. */
export interface SettleWavesAnswer {
  readonly runs: ImportRun[];
  /** Заголовок `Date` ответа; `null` — сервер его не прислал или он не разбирается. */
  readonly serverTime: Date | null;
}

/** Определённое «нет»: сервер ответил (4xx, кроме «повтори позже»), а не пропал. */
export function isDefiniteOff(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;
  return error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429;
}

function parseHttpDate(raw: string | null): Date | null {
  if (!raw) return null;
  const parsed = new Date(raw);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Прокси импорта персон в гейтвее (person-import.controller.ts): формы набора/правок/отчёта —
 *  SSoT в @lite-workspace/import-pipeline-core, сервис их не переупаковывает. */
@Injectable({ providedIn: 'root' })
export class PersonImportApiService {
  private readonly http = inject(HttpClient);

  /** Флаг PERSON_IMPORT_ENABLED тем же путём, что UPLOAD_CONFIRM_REPLACE_ENABLED: явное true — «вкл»,
   *  всё остальное (в том числе отсутствующий эндпоинт старого гейтвея) — «выкл». */
  isEnabled(): Observable<boolean> {
    return this.http.get<PersonImportConfig>('/api/config/person-import').pipe(
      map(config => config?.enabled === true),
      catchError(() => of(false)),
    );
  }

  preview(projectId: string, request: ImportPreviewRequest = {}): Observable<ImportPreviewResult> {
    return this.http.post<ImportPreviewResult>(`/api/projects/${projectId}/import/preview`, request);
  }

  apply(projectId: string, runId: string, overrides: ImportOverrides): Observable<ImportRun> {
    return this.http.post<ImportRun>(`/api/projects/${projectId}/import/runs/${runId}/apply`, { overrides });
  }

  /** 404 здесь — штатный ответ «импорт персон в проекте не заводился», а не сбой: каждое открытие
   *  проекта делает свой пробный запрос, и в аналитике они копились как ошибка. */
  listRuns(projectId: string): Observable<ImportRun[]> {
    return this.http.get<ImportRun[]>(`/api/projects/${projectId}/import/runs`, {
      context: new HttpContext().set(SKIP_ERROR_TRACKING, true),
    });
  }

  /** Досрочно дожимает волны проекта: не ждём крон, а закрываем прогоны сразу — ответ несёт их итог.
   *  `expectedForbidden` ставит вызывающий; оба нынешних (гейт фото, досчёт волн имён) ставят его всегда —
   *  403 тут штатный (демо-копия, не владелец), а смену identity замечают соседние запросы опроса. */
  settleWavesWithServerClock(
    projectId: string,
    options?: { readonly expectedForbidden?: boolean },
  ): Observable<SettleWavesAnswer> {
    const context = new HttpContext();
    // Ожидаемый отказ (демо-копия) — ответ, а не сбой: ни проверки identity, ни `error_shown`.
    if (options?.expectedForbidden) context.set(EXPECTED_FORBIDDEN, true).set(SKIP_ERROR_TRACKING_STATUSES, [403]);

    return this.http
      .post<ImportRun[]>(`/api/projects/${projectId}/import/waves/settle`, {}, { context, observe: 'response' })
      .pipe(map(response => ({ runs: response.body ?? [], serverTime: parseHttpDate(response.headers.get('Date')) })));
  }

  /** Тот же досчёт вызывающим, которым возраст прогона не нужен: часы сервера они бы всё равно
   *  выбросили, а форма ответа осталась бы у них в тестах. */
  settleWaves(projectId: string, options?: { readonly expectedForbidden?: boolean }): Observable<ImportRun[]> {
    return this.settleWavesWithServerClock(projectId, options).pipe(map(answer => answer.runs));
  }

  getRun(projectId: string, runId: string): Observable<ImportRun> {
    return this.http.get<ImportRun>(`/api/projects/${projectId}/import/runs/${runId}`);
  }
}
